import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import SCHEMA_CONSTANTS from 'vets-json-schema/dist/constants.json';
import { usePrevious } from 'platform/utilities/react-hooks';
import { apiRequest } from 'platform/utilities/api';
import environment from 'platform/utilities/environment';
import { focusElement } from 'platform/utilities/ui';
import { VaSelect } from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import { API_ENDPOINTS } from '../../utils/constants';

const STATES_USA = SCHEMA_CONSTANTS.formProfileStates;

// States without VA medical care services
const STATES_WITHOUT_MEDICAL = ['AA', 'AE', 'AP', 'FM', 'MH', 'PW'];

const stateOptions = STATES_USA.filter(
  state => !STATES_WITHOUT_MEDICAL.includes(state.value),
).map(state => (
  <option key={state.value} value={state.value}>
    {state.label}
  </option>
));

const VaMedicalCenter = props => {
  const { errorSchema, formContext, formData, idSchema, onChange } = props;
  const { submitted } = formContext;

  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const prevFacilityState = usePrevious(formData.state);
  const facilityState = formData.state;

  const handleStateChange = useCallback(
    event => {
      onChange({
        state: event.target.value,
        vaMedicalCenterName: undefined,
        city: undefined,
      });
    },
    [onChange],
  );

  const handleFacilityChange = useCallback(
    event => {
      const facilityName = event.target.value;
      const facility = facilities.find(f => f.name === facilityName);
      onChange({
        state: formData.state,
        vaMedicalCenterName: facilityName,
        city: facility?.city,
      });
    },
    [facilities, formData.state, onChange],
  );

  const fieldError = field =>
    submitted ? errorSchema[field]?.__errors?.[0] || null : null;

  useEffect(
    () => {
      if (!facilityState || facilityState === prevFacilityState) return;

      const fetchFacilities = async () => {
        setError(false);
        setLoading(true);
        setFacilities([]);
        try {
          const response = await apiRequest(
            environment.API_URL + API_ENDPOINTS.facilities + facilityState,
          );
          const facilityList = (response || [])
            .map(({ name, city }) => ({ name, city }))
            .sort((a, b) => a.name.localeCompare(b.name));
          setFacilities(facilityList);
        } catch (err) {
          setError(true);
          onChange({
            state: undefined,
            vaMedicalCenterName: undefined,
            city: undefined,
          });
          focusElement('.server-error-message');
        } finally {
          setLoading(false);
        }
      };

      fetchFacilities();
    },
    [facilityState, prevFacilityState, onChange],
  );

  const facilityOptions = useMemo(
    () =>
      facilities.map(facility => (
        <option key={facility.name} value={facility.name}>
          {facility.name}
        </option>
      )),
    [facilities],
  );

  return (
    <>
      {error && (
        <div className="server-error-message vads-u-margin-top--4">
          <va-alert status="error" uswds>
            <h2 slot="headline">Something went wrong on our end</h2>
            <p>
              We’re sorry. Something went wrong on our end. Please try selecting
              your state again.
            </p>
          </va-alert>
        </div>
      )}
      <VaSelect
        className="vads-u-margin-bottom--4"
        id={idSchema.state.$id}
        name={idSchema.state.$id}
        label="State"
        error={fieldError('state')}
        value={facilityState}
        onVaSelect={handleStateChange}
        required
      >
        {stateOptions}
      </VaSelect>

      {facilityState &&
        (loading ? (
          <va-loading-indicator
            label="Loading VA medical centers"
            message="Loading VA medical centers..."
          />
        ) : (
          <VaSelect
            className="vads-u-margin-bottom--4"
            id={idSchema.vaMedicalCenterName.$id}
            name={idSchema.vaMedicalCenterName.$id}
            label="Center or clinic"
            error={fieldError('vaMedicalCenterName')}
            value={formData.vaMedicalCenterName}
            onVaSelect={handleFacilityChange}
            required
          >
            {facilityOptions}
          </VaSelect>
        ))}
      <p className="vads-u-margin-bottom--4">
        <va-link
          href="/find-locations"
          external
          text="Find locations with the VA Facility Locator"
        />
      </p>
    </>
  );
};

VaMedicalCenter.propTypes = {
  errorSchema: PropTypes.object,
  formContext: PropTypes.object,
  formData: PropTypes.object,
  idSchema: PropTypes.object,
  schema: PropTypes.object,
  onChange: PropTypes.func,
};

export default VaMedicalCenter;
